import * as React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

import Loader from "./Loader";
import tw from "twin.macro";

interface ButtonElProps {
  variant?: "primary" | "secondary" | "worry" | "neutral" | "outline";
  size?: "xBig" | "big" | "small" | "medium";
  iconRight?: boolean;
  fullWidth?: boolean;
  iconOnly?: boolean;
  loading?: boolean;
}

interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  text?: React.ReactNode;
  icon?: React.ReactNode;
}

function Button(
  props: React.PropsWithChildren<ButtonElProps & Props>,
  ref: any
) {
  const { text, children, icon, loading, ...rest } = props;

  const hasTextOrChildren = !!text || !!children;

  return (
    <ButtonEl ref={ref} disabled={loading || rest.disabled} {...rest}>
      {loading ? (
        <Loader
          size="20"
          color={rest.variant !== "secondary" ? "#fff" : undefined}
        />
      ) : (
        <>
          {icon !== undefined ? icon : null}
          {hasTextOrChildren ? <span>{text || children}</span> : null}
        </>
      )}
    </ButtonEl>
  );
}

export default React.forwardRef(Button);

const ButtonEl = styled.button<ButtonElProps>`
  border: none;
  outline: none;
  cursor: pointer;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0px 15px;

  ${(props) => {
    switch (props.variant) {
      case "outline":
        return css`
          ${tw`text-dark`};

          border: 1px solid #e6e7e7;
        `;
      case "secondary":
        return css`
          ${tw`text-text-on-secondary`};
        `;
      case "primary":
      case "worry":
      case "neutral":
      default:
        return css`
          ${tw`text-white`};
        `;
    }
  }};

  ${(props) => {
    switch (props.variant) {
      case "primary":
        return css`
          ${tw`bg-prime`}
        `;
      case "secondary":
        return css`
          ${tw`bg-action-secondary`};
        `;
      case "worry":
        return css`
          ${tw`bg-red`};
        `;
      case "neutral":
        return css`
          ${tw`bg-dark`};
        `;
    }
  }};

  ${(props) => {
    switch (props.size) {
      case "xBig":
        return css`
          border-radius: 999px;
          height: 56px;
          font-size: 1.6rem;
          max-width: 100%;
        `;

      case "big":
        return css`
          border-radius: 999px;
          height: 50px;
          font-size: 1.6rem;
          width: 320px;
          max-width: 100%;
        `;
      case "medium":
        return css`
          border-radius: 999px;
          height: 40px;
          font-size: 1.4rem;
        `;

      case "small":
      default:
        return css`
          border-radius: 999px;
          height: 40px;
          font-size: 1.4rem;
        `;
    }
  }};

  > svg {
    order: 0;
    ${(props) =>
      props.iconRight
        ? css`
            margin-left: 10px;
            order: 1;
          `
        : css`
            margin-right: 10px;
          `};
  }

  > svg:only-child {
    margin: 0;
  }

  ${(props) =>
    props.iconOnly &&
    css`
      justify-content: center;
      align-items: center;
      width: 40px;
      padding: 0px;

      > svg {
        ${tw`w-full h-full`};

        flex-shrink: 0;
      }
    `};

  ${(props) =>
    props.fullWidth &&
    css`
      width: 100%;
    `};

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;
